/**
 * VersionHistory — Inline arrow navigation for canvas tab versions.
 * Shows: ◄  v2 / 3 (latest)  ► with disabled arrows at boundaries.
 */

import { useState, useEffect, useRef } from 'react'
import { Button } from '../ui/button'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { CanvasTab, CanvasVersion } from '../../hooks/useCanvas'

interface VersionHistoryProps {
  tab: CanvasTab
  versions: CanvasVersion[]
  onRestore: (tab: CanvasTab, versionId: string) => void
}

export function VersionHistory({ tab, versions, onRestore }: VersionHistoryProps) {
  const total = versions.length
  const prevLengthRef = useRef(total)
  const [currentIndex, setCurrentIndex] = useState(total > 0 ? total - 1 : 0)

  // Reset to latest when a new version is added (length changes)
  useEffect(() => {
    if (total !== prevLengthRef.current) {
      setCurrentIndex(total > 0 ? total - 1 : 0)
      prevLengthRef.current = total
    }
  }, [total])

  if (total === 0) return null

  const isFirst = currentIndex === 0
  const isLatest = currentIndex === total - 1

  const navigateTo = (newIndex: number) => {
    setCurrentIndex(newIndex)
    onRestore(tab, versions[newIndex].id)
  }

  return (
    <div className="flex items-center gap-1">
      <Button
        size="icon"
        variant="ghost"
        className="h-6 w-6"
        disabled={isFirst}
        onClick={() => navigateTo(currentIndex - 1)}
        title="Previous version"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </Button>

      <span className="text-xs text-muted-foreground whitespace-nowrap select-none">
        v{currentIndex + 1} / {total}{isLatest ? ' (latest)' : ''}
      </span>

      <Button
        size="icon"
        variant="ghost"
        className="h-6 w-6"
        disabled={isLatest}
        onClick={() => navigateTo(currentIndex + 1)}
        title="Next version"
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  )
}
